import type { DashboardStatus, RequestLogEntry } from "@/types/dashboard";
import { truncateText } from "@/utils/format";
import { isSystemRequestLogEntry } from "@/utils/requestLog";

export type SiteNotification = {
  kind: "bad" | "warn" | "good";
  ts: number;
  title: string;
  text: string;
  meta: string;
};

function fmtRelative(ts: number): string {
  const n = Number(ts || 0);
  if (!Number.isFinite(n) || n <= 0) return "just now";
  const diff = Math.max(0, Date.now() - n);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return sec <= 5 ? "just now" : `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function logSourceBucket(e: RequestLogEntry): string {
  if (e.sourceBucket === "browser_cache" || e.source === "browser_cache") return "browser_cache";
  if (e.sourceBucket === "nginx" || e.sourceBucket === "node" || e.sourceBucket === "origin") return e.sourceBucket;
  const src = String(e.source || "");
  if (src === "browser_cache") return "browser_cache";
  if (src === "cache" || src === "cache_staging") return "node";
  if (src === "nginx_mirror") return "nginx";
  if (src.startsWith("upstream")) return "origin";
  return src ? "node" : "";
}

function logSourceLabel(bucket: string): string {
  if (bucket === "browser_cache") return "Browser cache";
  if (bucket === "node") return "SW App";
  if (bucket === "nginx") return "SW Edge";
  if (bucket === "origin") return "Origin";
  if (bucket === "cloudfront") return "CloudFront";
  return bucket || "";
}

export function buildSiteNotifications(
  status: DashboardStatus | null,
  logs: RequestLogEntry[],
  activeSite: string
): SiteNotification[] {
  const out: SiteNotification[] = [];
  const st = (status || {}) as Record<string, unknown>;
  const site =
    activeSite ||
    String((st.domain as Record<string, unknown> | undefined)?.currentHost || "") ||
    "this site";

  try {
    const leads = st.leads as Record<string, unknown> | undefined;
    const unhandled = Number(leads?.unhandled || 0) || 0;
    if (unhandled > 0) {
      out.push({
        kind: "warn",
        ts: Date.now(),
        title: `${unhandled} unhandled lead${unhandled === 1 ? "" : "s"}`,
        text: `Review new leads for ${site}.`,
        meta: "Leads"
      });
    }
  } catch { /* ignore */ }

  try {
    const origin = st.origin as Record<string, unknown> | undefined;
    const originDomains = Array.isArray(origin?.domains) ? origin.domains as Record<string, unknown>[] : [];
    originDomains.forEach((d) => {
      const domain = String(d.domain || site);
      const httpStatus = Number(d.status || 0) || 0;
      const ok = Boolean(d.ok === true || d.healthy === true) || (httpStatus >= 200 && httpStatus < 400);
      if (!ok) {
        out.push({
          kind: "bad",
          ts: Date.now(),
          title: "Origin needs attention",
          text: domain + (httpStatus ? ` returned HTTP ${httpStatus}` : " did not report healthy."),
          meta: "Origin health"
        });
      }
    });
  } catch { /* ignore */ }

  try {
    const requests = st.requests as Record<string, unknown> | undefined;
    const last5 = requests?.last5min as Record<string, unknown> | undefined;
    const cls = (last5?.byStatusClass || {}) as Record<string, number>;
    const five = Number(cls["5xx"] || 0) || 0;
    const four = Number(cls["4xx"] || 0) || 0;
    if (five > 0) {
      out.push({
        kind: "bad",
        ts: Date.now(),
        title: `${five} server error${five === 1 ? "" : "s"} in 5 min`,
        text: "Recent requests include HTTP 5xx responses.",
        meta: "Request health"
      });
    }
    if (four > 10) {
      out.push({
        kind: "warn",
        ts: Date.now(),
        title: `${four} client/security responses in 5 min`,
        text: "High 4xx activity can indicate scanners or broken links.",
        meta: "Request health"
      });
    }
  } catch { /* ignore */ }

  try {
    const firewall = st.firewall as Record<string, unknown> | undefined;
    const last24h = firewall?.last24h as Record<string, unknown> | undefined;
    const fwRecent = Array.isArray(last24h?.recent) ? last24h.recent as Record<string, unknown>[] : [];
    fwRecent.slice(-8).reverse().forEach((ev) => {
      const action = String(ev.action || ev.type || "firewall");
      const ip = String(ev.ip || "").trim();
      const path = String(ev.path || ev.url || "").trim();
      out.push({
        kind: action.includes("ban") || action.includes("block") ? "bad" : "warn",
        ts: Number(ev.ts || ev.atMs || ev.timeMs || Date.now()),
        title: action.replace(/^firewall_/, "").replace(/_/g, " "),
        text: [ip, path].filter(Boolean).join(" · ") || "Firewall activity detected.",
        meta: "Firewall"
      });
    });
  } catch { /* ignore */ }

  try {
    logs.slice(0, 60).forEach((e) => {
      if (!e || isSystemRequestLogEntry(e)) return;
      const httpStatus = Number(e.status || 0) || 0;
      const src = String(e.source || "");
      const important = httpStatus >= 500 || src.startsWith("firewall_") || src === "form_protection";
      if (!important) return;
      out.push({
        kind: httpStatus >= 500 ? "bad" : "warn",
        ts: Number(e.ts || Date.now()),
        title: httpStatus >= 500 ? `HTTP ${httpStatus} error` : logSourceLabel(logSourceBucket(e)),
        text: [String(e.ip || "").trim(), String(e.url || "").trim()].filter(Boolean).join(" · "),
        meta: "Request log"
      });
    });
  } catch { /* ignore */ }

  out.sort((a, b) => b.ts - a.ts);
  return out.slice(0, 12);
}

export function notificationMetaLine(item: SiteNotification): string {
  return [item.meta || "Site", fmtRelative(item.ts)].filter(Boolean).join(" · ");
}

export { fmtRelative, truncateText };
